import { useEffect, useState } from "react";
import { Modal, Form, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { agent } from "../../utils/agent";
import * as API from "../../utils/apiPath";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import toggleNetworkRequestStatus from "../../actions/toggleNetworkRequestStatus";
import { loadData } from "../../actions/scheduleListingAction";
import moment from "moment";
import PopupModalWithOk from "../../utils/helpers/popupModalOk";
import PropTypes from "prop-types";
import {
  buildMeetingRequest,
  getRedirectPath,
  getSubmitButtonText,
  getFormClassName,
  getDefaultStartTime,
  getUserDisplayData,
  getDefaultMeetingDate2,
} from "./scheduleMeetingModalHelper";

const ScheduleMeetingModal = (props) => {
  const { scheduleUser, scheduleData, type, updatePage, setting } = props;
  const userInfo = useSelector((state) => state.userInfo);
  const dispatch = useDispatch();
  const [timezone, setTimezone] = useState([]);
  const [apiLoader, setApiLoader] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const history = useNavigate();
  const defaultTimezone =
    setting?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm();

  useEffect(() => {
    if (type === "edit" && scheduleData?.meeting_timezone) {
      setValue("meeting_timezone", scheduleData.meeting_timezone);
    } else if (defaultTimezone) {
      setValue("meeting_timezone", defaultTimezone);
    }
  }, [type, scheduleData, defaultTimezone, setValue]);

  const handleApiResult = (message, navigateFlag) => {
    setPopupMessage(message);
    setShouldNavigate(navigateFlag);
    setShowPopup(true);
  };

  const onSubmit = (data) => {
    dispatch(toggleNetworkRequestStatus(true));
    setApiLoader(true);

    const request = buildMeetingRequest(data, type, scheduleData, scheduleUser);
    agent
      .post(`${API.LOAD_CUSTOMER}/web-save-meeting`, request)
      .then((res) => handleApiResult(res.data.message, true))
      .catch(() =>
        handleApiResult("Something went wrong. Please try again.", false)
      )
      .finally(() => {
        setApiLoader(false);
        dispatch(toggleNetworkRequestStatus(false));
        dispatch(loadData({}));
      });
  };

  const handlePopupOk = () => {
    setShowPopup(false);
    if (!shouldNavigate) return;
    props.onHide();
    dispatch(loadData({}));
    history(getRedirectPath(type, updatePage, userInfo, scheduleUser));
  };

  const submitButtonText = getSubmitButtonText(type, apiLoader);
  const userDisplayData = getUserDisplayData(
    type,
    scheduleData,
    scheduleUser,
    userInfo
  );

  useEffect(() => {
    const fetchTimezone = async () => {
      try {
        const response = await agent.get(API.LOAD_CALENDAR_TIMEZONE);
        if (response.status === 200) {
          const tzList = response.data?.data || [];
          setTimezone(tzList);
        }
      } catch (error) {
        console.error("Error loading timezone:", error);
      }
    };

    fetchTimezone();
  }, []);

  useEffect(() => {
    if (!timezone.length) return;

    if (type === "edit" && scheduleData?.meeting_timezone) {
      setValue("meeting_timezone", scheduleData.meeting_timezone);
    } else if (defaultTimezone) {
      setValue("meeting_timezone", defaultTimezone);
    }
  }, [timezone, type, scheduleData, defaultTimezone, setValue]);

  return (
    <div className="">
      <Modal
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered className="calendar-modal self_asset_modal"
      >
        <Modal.Header closeButton className="mb-4 close-btn-modal">Schedule Meeting</Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="mentor_detail align-items-center">
              <Col sm={12} className="mb-4">
                <label className="form-label" htmlFor="subject">
                  Topic *
                </label>
                <Form.Control
                  type="text"
                  className={getFormClassName(errors?.subject)}
                  defaultValue={type === "edit" ? scheduleData?.subject : ""}
                  name="subject"
                  {...register("subject", {
                    required: true,
                    maxLength: {
                      value: 255,
                    },
                  })}
                />
                {errors?.subject && errors?.subject?.type === "required" && (
                  <label
                    className="customeValidationText text-left"
                    htmlFor="subject"
                  >
                    Please enter subject.
                  </label>
                )}
                {errors?.subject && errors?.subject?.type === "maxLength" && (
                  <label
                    className="customeValidationText text-left"
                    htmlFor="subject"
                  >
                    Subject maximum length is 255 characters.
                  </label>
                )}
              </Col>
            </Row>
            <Row className="mentor_detail align-items-center">
              <Col sm={12} className="mb-4">
                <label className="form-label" htmlFor="description">
                  Description *
                </label>
                <Form.Control
                  as="textarea"
                  className={getFormClassName(errors?.description)}
                  defaultValue={type === "edit" ? scheduleData?.description : ""}
                  name="description"
                  placeholder="Please use this section to share details of the proposed meeting and meetings links / zoom / Teams etc."
                  aria-invalid={errors?.description}
                  {...register("description", {
                    required: {
                      value: true,
                      message: "Please enter description.",
                    },
                    validate: (value) => {
                      if (value.trim() === "") {
                        return "Please enter description.";
                      }
                      return true;
                    },
                  })}
                />
                {errors?.description?.message && (
                  <label
                    className="customeValidationText text-left"
                    htmlFor="description"
                  >
                    {errors?.description?.message}
                  </label>
                )}
              </Col>
            </Row>
            <Row className="mentor_detail align-items-center">
              <Col sm={12} className="mb-4">
                <label className="form-label" htmlFor="meeting_timezone">
                  Timezone *
                </label>
                <select
                  className={getFormClassName(errors?.meeting_timezone)}
                  {...register("meeting_timezone", {
                    required: {
                      value: true,
                      message: "Please select timezone.",
                    },
                  })}
                >
                  <option value="">Select Meeting Type</option>

                  {timezone.map((timezoneObject) => {
                    const key = Object.keys(timezoneObject)[0];   // e.g. "Africa/Conakry"
                    const value = timezoneObject[key];            // e.g. "(GMT+0) Africa/Conakry"

                    return (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    );
                  })}
                </select>

                {errors?.meeting_timezone?.message && (
                  <label
                    className="customeValidationText text-left"
                    htmlFor="meeting_timezone"
                  >
                    {errors?.meeting_timezone?.message}
                  </label>
                )}
              </Col>
            </Row>
            <Row className="mentor_detail align-items-center mb-4">
              <Col sm={4}>
                <label className="form-label" htmlFor="meeting_date">
                  Meeting Date *
                </label>
                <Form.Control
                  type="date"
                  className={getFormClassName(errors?.meeting_date)}
                  defaultValue={getDefaultMeetingDate2(type, scheduleData, moment)}
                  min={moment().format("YYYY-MM-DD")}
                  {...register("meeting_date", {
                    required: {
                      value: true,
                      message: "Please select meeting date.",
                    },
                  })}
                />
                {errors?.meeting_date?.message && (
                  <label
                    className="customeValidationText text-left"
                    htmlFor="meeting_date"
                  >
                    {errors?.meeting_date?.message}
                  </label>
                )}
              </Col>
              <Col sm={4}>
                <label className="form-label" htmlFor="meeting_start_time">
                  Start Time *
                </label>
                <Form.Control
                  type="time"
                  className={getFormClassName(errors?.meeting_start_time)}
                  defaultValue={getDefaultStartTime(type, scheduleData, moment)}
                  {...register("meeting_start_time", {
                    required: {
                      value: true,
                      message: "Please select start time.",
                    },
                  })}
                />
                {errors?.meeting_start_time?.message && (
                  <label
                    className="customeValidationText text-left"
                    htmlFor="meeting_start_time"
                  >
                    {errors?.meeting_start_time?.message}
                  </label>
                )}
              </Col>
              <Col sm={4}>
                <label className="form-label" htmlFor="meeting_end_time">
                  Duration (In Hour) *
                </label>
                <select
                  name="meeting_end_time"
                  className={getFormClassName(errors?.meeting_end_time)}
                  defaultValue={type === "edit" ? scheduleData?.meeting_end_time : ""}
                  {...register("meeting_end_time", {
                    required: {
                      value: true,
                      message: "Please select duration.",
                    },
                    validate: (value) => {
                      if (value.trim() === "") {
                        return "Please select duration.";
                      }
                      return true;
                    },
                  })}
                >
                  <option value="">Select Duration</option>
                  <option
                    value="1"
                    selected={scheduleData?.meeting_end_time === "1" ? "selected" : ""}
                  >
                    1
                  </option>
                  <option
                    value="2"
                    selected={scheduleData?.meeting_end_time === "2" ? "selected" : ""}
                  >
                    2
                  </option>
                  <option
                    value="3"
                    selected={scheduleData?.meeting_end_time === "3" ? "selected" : ""}
                  >
                    3
                  </option>
                  <option
                    value="4"
                    selected={scheduleData?.meeting_end_time === "4" ? "selected" : ""}
                  >
                    4
                  </option>
                  <option
                    value="5"
                    selected={scheduleData?.meeting_end_time === "5" ? "selected" : ""}
                  >
                    5
                  </option>
                  <option
                    value="5+"
                    selected={scheduleData?.meeting_end_time === "5+" ? "selected" : ""}
                  >
                    More than 5
                  </option>
                </select>
                {errors?.meeting_end_time?.message && (
                  <label
                    className="customeValidationText text-left"
                    htmlFor="meeting_end_time"
                  >
                    Please select duration.
                  </label>
                )}
              </Col>
            </Row>
            <Row className="mentor_detail align-items-center">
              <Col md={12}>
                <label className="form-label" htmlFor="user-display">
                  <b>{userDisplayData.label}</b>
                </label>
                <div className="rounder-img">
                  <img src={userDisplayData.image} alt="" />
                  <span>{userDisplayData.name}</span>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className="form-group mt-3 text-left">
                  <button
                    className="btn loginsubmit btn_red nextbtn me-2"
                    type="submit"
                    disabled={apiLoader}
                  >
                    {submitButtonText}
                  </button>
                  <button
                    className="btn loginsubmit nextbtn btn_red me-2 transparent-btn-cancel"
                    onClick={() => {
                      props.onHide();
                      setApiLoader(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body >
      </Modal >
      <PopupModalWithOk
        show={showPopup}
        onHide={() => setShowPopup(false)}
        message={popupMessage}
        showConfirm={true}
        staticBackdrop={true}
        onConfirm={handlePopupOk}
      />
    </div>
  );
};

ScheduleMeetingModal.propTypes = {
  scheduleUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mentor_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mentee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mentee_image: PropTypes.string,
    mentee_name: PropTypes.string,
    mentors_image: PropTypes.string,
    mentor_name: PropTypes.string,
  }).isRequired,
  scheduleData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    relation_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    meeting_date: PropTypes.string,
    meeting_start_time: PropTypes.string,
    meeting_end_time: PropTypes.string,
    subject: PropTypes.string,
    description: PropTypes.string,
    meeting_timezone: PropTypes.string,
    creator_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    receiver_mentor_role_id: PropTypes.string,
    receiver_image_path: PropTypes.string,
    receiver_name: PropTypes.string,
    creator_mentor_role_id: PropTypes.string,
    creator_image_path: PropTypes.string,
    creator_name: PropTypes.string,
    meeting_start_datetime_converted: PropTypes.string,
  }).isRequired,
  type: PropTypes.string.isRequired,
  updatePage: PropTypes.string,
  setting: PropTypes.shape({
    timezone: PropTypes.string,
  }).isRequired,
  onHide: PropTypes.func.isRequired,
};

export default ScheduleMeetingModal;
